import { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import * as XLSX from 'xlsx';
import {
  BarChart3,
  CheckCircle2,
  Clock,
  FileSpreadsheet,
  Printer,
  RefreshCw,
  Search,
  Users,
  X,
  Filter,
} from 'lucide-react';

import { TypographyH1, TypographyMuted } from '@/components/ui/typography';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { DebouncedInput } from '@/components/DebouncedInput';
import { MultiSelect } from '@/components/MultiSelect';
import { useSellerCoverageReportQuery } from '@/queries/useSellerCoverageReportQuery';
import { useMarketResearchQuery } from '@/queries/useMarketResearchQuery';
import { useResaleQuery } from '@/queries/useResaleQuery';
import { useBaseStore } from '@/store';
import { Store } from '@/store/type';
import LoadingScreen from '@/pages/LoadingScreen';
import SellerCoverageItemsDialog from './components/SellerCoverageItemsDialog';
import {
  SellerCoverageItem,
  FetchSellerCoveragePayload,
} from '@/api/business/marketResearch';

const stateSelector = (state: Store) => ({
  profile: state.authSlice.state.profile,
});

const SellerCoverageReport = () => {
  const [searchParams] = useSearchParams();
  const initialResearchId = searchParams.get('search')
    ? Number(searchParams.get('search'))
    : 0;

  const { profile } = useBaseStore(stateSelector);

  const [selectedPesquisas, setSelectedPesquisas] = useState<string[]>(
    initialResearchId > 0 ? [String(initialResearchId)] : [],
  );
  const [selectedResales, setSelectedResales] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');

  // Modal state for items
  const [selectedSellerForItems, setSelectedSellerForItems] =
    useState<SellerCoverageItem | null>(null);
  const [isItemsDialogOpen, setIsItemsDialogOpen] = useState<boolean>(false);

  // Initial search activation state
  const [hasSearched, setHasSearched] = useState<boolean>(
    initialResearchId > 0,
  );
  const [appliedPayload, setAppliedPayload] =
    useState<FetchSellerCoveragePayload>(() => {
      const p: FetchSellerCoveragePayload = {};
      if (initialResearchId > 0) {
        p.SK_PESQUISA = initialResearchId;
        p.SK_PESQUISAS = [initialResearchId];
      }
      return p;
    });

  // Fetch drop options
  const { data: researches } = useMarketResearchQuery();
  const { data: resales } = useResaleQuery();

  const {
    data: reportData,
    isLoading,
    isFetching,
    refetch,
  } = useSellerCoverageReportQuery(appliedPayload, {
    enabled: hasSearched,
  });

  const handleTogglePesquisa = (val: string) => {
    setSelectedPesquisas((prev) =>
      prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val],
    );
  };

  const handleSelectAllPesquisas = (values: string[]) => {
    setSelectedPesquisas((prev) =>
      prev.length === values.length ? [] : values,
    );
  };

  const handleToggleResale = (val: string) => {
    setSelectedResales((prev) =>
      prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val],
    );
  };

  const handleSelectAllResales = (values: string[]) => {
    setSelectedResales((prev) => (prev.length === values.length ? [] : values));
  };

  const handleSearch = () => {
    const p: FetchSellerCoveragePayload = {};
    if (selectedPesquisas.length > 0) {
      p.SK_PESQUISAS = selectedPesquisas.map(Number);
    }
    if (selectedResales.length > 0) {
      p.SK_REVENDAS = selectedResales.map(Number);
    }
    if (searchTerm.trim()) {
      p.SEARCH_TERM = searchTerm.trim();
    }
    setAppliedPayload(p);
    setHasSearched(true);
  };

  // Options for Pesquisa MultiSelect
  const pesquisaOptions = useMemo(() => {
    if (!researches) return [];
    return researches.map((r) => ({
      value: String(r.SK_PESQUISA),
      label: `${r.NM_PESQUISA} (${r.SK_PESQUISA})`,
    }));
  }, [researches]);

  // Options for Resale MultiSelect
  const resaleOptions = useMemo(() => {
    if (!resales) return [];
    return resales.map((r) => ({
      value: String(r.SK_REVENDA),
      label: r.DS_REVENDA,
    }));
  }, [resales]);

  const filteredReport = hasSearched && reportData ? reportData : [];

  // Totalizers (RF003 / RN002)
  const totals = useMemo(() => {
    const previstos = filteredReport.reduce(
      (acc, curr) => acc + (curr.QTD_CLIENTES_PREVISTOS || 0),
      0,
    );
    const realizadas = filteredReport.reduce(
      (acc, curr) => acc + (curr.QTD_PESQUISAS_REALIZADAS || 0),
      0,
    );
    const pendentes = filteredReport.reduce(
      (acc, curr) => acc + (curr.QTD_PESQUISAS_PENDENTES || 0),
      0,
    );
    const cobertura =
      previstos > 0 ? Number(((realizadas / previstos) * 100).toFixed(2)) : 0;

    return {
      previstos,
      realizadas,
      pendentes,
      cobertura,
    };
  }, [filteredReport]);

  // Export to Excel (RF004 / RN003)
  const handleExportExcel = () => {
    if (!filteredReport || filteredReport.length === 0) return;

    const exportData = filteredReport.map((item) => ({
      'Código Vendedor': item.NK_FUNCIONARIO || item.SK_VENDEDOR,
      'Nome Vendedor': item.NM_FUNCIONARIO,
      Revenda: item.DS_REVENDA,
      Pesquisa: item.NM_PESQUISA,
      'Clientes Previstos': item.QTD_CLIENTES_PREVISTOS,
      'Pesquisas Realizadas': item.QTD_PESQUISAS_REALIZADAS,
      'Pesquisas Pendentes': item.QTD_PESQUISAS_PENDENTES,
      '% Cobertura': `${item.PERC_COBERTURA.toFixed(2)}%`,
    }));

    // Append total row
    exportData.push({
      'Código Vendedor': 'TOTAL GERAL',
      'Nome Vendedor': '-',
      Revenda: '-',
      Pesquisa: '-',
      'Clientes Previstos': totals.previstos,
      'Pesquisas Realizadas': totals.realizadas,
      'Pesquisas Pendentes': totals.pendentes,
      '% Cobertura': `${totals.cobertura.toFixed(2)}%`,
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Cobertura por Vendedor');
    XLSX.writeFile(
      workbook,
      `relatorio_cobertura_vendedor_${new Date().toISOString().slice(0, 10)}.xlsx`,
    );
  };

  // Print report
  const handlePrint = () => {
    window.print();
  };

  const hasActiveFilters =
    selectedPesquisas.length > 0 ||
    selectedResales.length > 0 ||
    searchTerm !== '' ||
    hasSearched;

  const handleClearFilters = () => {
    setSelectedPesquisas([]);
    setSelectedResales([]);
    setSearchTerm('');
    setAppliedPayload({});
    setHasSearched(false);
  };

  if (hasSearched && isLoading) {
    return <LoadingScreen />;
  }

  return (
    <div className="flex flex-col h-full p-4 space-y-6 overflow-auto md:p-6 bg-gray-50 print:p-0 print:bg-white print:overflow-visible">
      {/* Header Section */}
      <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center print:hidden">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center shadow-sm w-11 h-11 rounded-xl bg-primary/10 text-primary">
            <BarChart3 className="w-6 h-6" />
          </div>
          <div>
            <TypographyH1 className="text-2xl font-bold tracking-tight text-gray-900">
              Relatório de Cobertura por Vendedor
            </TypographyH1>
            <TypographyMuted className="text-sm">
              Acompanhe o desempenho de execução e percentual de cobertura das
              pesquisas
            </TypographyMuted>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
            className="gap-2 text-gray-700 bg-white border-gray-300 hover:bg-gray-100"
          >
            <RefreshCw
              className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`}
            />
            <span>Atualizar</span>
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={handleExportExcel}
            disabled={filteredReport.length === 0}
            className="gap-2 text-emerald-700 bg-emerald-50 border-emerald-200 hover:bg-emerald-100"
          >
            <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
            <span>Exportar Excel</span>
          </Button>

          <Button
            variant="default"
            size="sm"
            onClick={handlePrint}
            className="gap-2 text-white shadow-sm bg-primary hover:bg-primary/90"
          >
            <Printer className="w-4 h-4" />
            <span>Imprimir</span>
          </Button>
        </div>
      </div>

      {/* Print Only Header */}
      <div className="hidden pb-4 mb-6 text-center border-b print:block">
        <h1 className="text-2xl font-bold text-gray-900">
          Relatório de Cobertura por Vendedor
        </h1>
        <p className="text-sm text-gray-500">
          Gerado em: {new Date().toLocaleDateString('pt-BR')}{' '}
          {new Date().toLocaleTimeString('pt-BR')}
        </p>
      </div>

      {/* Filters Section */}
      <div className="p-4 space-y-4 bg-white border border-gray-200 shadow-sm rounded-xl print:hidden">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <Filter className="w-4 h-4 text-gray-500" />
            <span>Filtros de Pesquisa</span>
          </div>

          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearFilters}
              className="h-8 gap-1 px-2 text-xs text-muted-foreground hover:text-foreground"
            >
              <X className="w-3.5 h-3.5" />
              Limpar Filtros
            </Button>
          )}
        </div>

        <div className="grid items-end grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {/* Pesquisa MultiSelect */}
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1.5 block">
              Pesquisas (
              {selectedPesquisas.length > 0
                ? selectedPesquisas.length
                : 'Todas'}
              )
            </label>
            <MultiSelect
              label="Todas as Pesquisas"
              placeholder="Buscar pesquisa..."
              emptyLabel="Nenhuma pesquisa encontrada"
              options={pesquisaOptions}
              selectedValues={selectedPesquisas}
              onChange={handleTogglePesquisa}
              onSelectAll={handleSelectAllPesquisas}
            />
          </div>

          {/* Revenda MultiSelect (Only for Admins) */}
          {profile !== 'REPRESENTANTE' ? (
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1.5 block">
                Revendas (
                {selectedResales.length > 0 ? selectedResales.length : 'Todas'})
              </label>
              <MultiSelect
                label="Todas as Revendas"
                placeholder="Buscar revenda..."
                emptyLabel="Nenhuma revenda encontrada"
                options={resaleOptions}
                selectedValues={selectedResales}
                onChange={handleToggleResale}
                onSelectAll={handleSelectAllResales}
              />
            </div>
          ) : (
            <div />
          )}

          {/* Vendedor Text Search */}
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1.5 block">
              Buscar Vendedor
            </label>
            <div className="relative">
              <Search className="absolute w-4 h-4 text-gray-400 -translate-y-1/2 left-3 top-1/2" />
              <DebouncedInput
                value={searchTerm}
                onChange={(val) => setSearchTerm(String(val))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSearch();
                }}
                placeholder="Nome ou matrícula..."
                className="w-full h-10 text-sm bg-white border border-gray-200 rounded-md pl-9 focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>
          </div>

          {/* Search Action Button */}
          <div>
            <Button
              onClick={handleSearch}
              disabled={isFetching}
              className="w-full h-10 gap-2 text-white shadow-sm bg-primary hover:bg-primary/90"
            >
              <Search
                className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`}
              />
              <span>{isFetching ? 'Consultando...' : 'Consultar'}</span>
            </Button>
          </div>
        </div>
      </div>

      {/* KPI Cards (RF002) */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="transition-shadow bg-white border-gray-200 shadow-sm hover:shadow">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs font-semibold tracking-wider text-gray-500 uppercase">
              Clientes Previstos
            </CardTitle>
            <div className="p-2 text-blue-600 rounded-lg bg-blue-50">
              <Users className="w-5 h-5" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">
              {totals.previstos}
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Total atribuído na meta
            </p>
          </CardContent>
        </Card>

        <Card className="transition-shadow bg-white border-gray-200 shadow-sm hover:shadow">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs font-semibold tracking-wider uppercase text-emerald-600">
              Pesquisas Realizadas
            </CardTitle>
            <div className="p-2 rounded-lg bg-emerald-50 text-emerald-600">
              <CheckCircle2 className="w-5 h-5" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-700">
              {totals.realizadas}
            </div>
            <p className="mt-1 text-xs text-emerald-600">
              Pesquisas concluídas
            </p>
          </CardContent>
        </Card>

        <Card className="transition-shadow bg-white border-gray-200 shadow-sm hover:shadow">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs font-semibold tracking-wider uppercase text-amber-600">
              Pesquisas Pendentes
            </CardTitle>
            <div className="p-2 rounded-lg bg-amber-50 text-amber-600">
              <Clock className="w-5 h-5" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-700">
              {totals.pendentes}
            </div>
            <p className="mt-1 text-xs text-amber-600">Faltantes para a meta</p>
          </CardContent>
        </Card>

        <Card className="transition-shadow bg-white border-gray-200 shadow-sm hover:shadow">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs font-semibold tracking-wider uppercase text-primary">
              Cobertura Geral
            </CardTitle>
            <div className="p-2 rounded-lg bg-primary/10 text-primary">
              <BarChart3 className="w-5 h-5" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary">
              {totals.cobertura.toFixed(2)}%
            </div>
            <Progress
              value={totals.cobertura}
              className="h-2 mt-2 bg-primary/10"
            />
          </CardContent>
        </Card>
      </div>

      {/* Data Table */}
      <div className="flex-1 bg-white border border-gray-200 shadow-sm rounded-xl print:border-none print:shadow-none">
        <div className="w-full">
          <div className="h-[70dvh] overflow-auto">
            <table className="w-full text-sm text-left border-collapse min-w-[800px]">
              <thead className="sticky top-0 z-10">
                <tr className="font-semibold text-gray-700 bg-gray-100 border-b border-gray-200">
                  <th className="p-3.5 pl-4 min-w-[200px]">Vendedor</th>
                  <th className="p-3.5 min-w-[140px]">Revenda</th>
                  <th className="p-3.5 min-w-[180px]">Pesquisa</th>
                  <th className="p-3.5 text-center min-w-[90px]">Previstos</th>
                  <th className="p-3.5 text-center min-w-[90px]">Realizados</th>
                  <th className="p-3.5 text-center min-w-[90px]">Pendentes</th>
                  <th className="p-3.5 pr-4 text-right min-w-[140px]">
                    % Cobertura
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100">
                {!hasSearched ? (
                  <tr>
                    <td colSpan={7} className="py-16 text-center text-gray-500">
                      <div className="flex flex-col items-center justify-center gap-2">
                        <div className="p-3 text-gray-400 bg-gray-100 rounded-full">
                          <Filter className="w-6 h-6" />
                        </div>
                        <p className="font-semibold text-gray-700">
                          Nenhuma consulta realizada
                        </p>
                        <p className="max-w-sm text-xs text-gray-500">
                          Selecione os filtros desejados acima e clique em{' '}
                          <strong>Consultar</strong> para carregar os dados de
                          cobertura por vendedor.
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : filteredReport.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-10 text-center text-gray-500">
                      Nenhum registro de cobertura encontrado para os filtros
                      selecionados.
                    </td>
                  </tr>
                ) : (
                  filteredReport.map((item, idx) => (
                    <tr
                      key={idx}
                      className="transition-colors hover:bg-gray-50/80"
                    >
                      <td className="p-3.5 pl-4 font-medium text-gray-900">
                        <div className="font-semibold">
                          {item.NM_FUNCIONARIO}
                        </div>
                        <div className="font-mono text-xs text-gray-500">
                          Cód: {item.NK_FUNCIONARIO || item.SK_VENDEDOR}
                        </div>
                      </td>
                      <td className="p-3.5 text-gray-600">{item.DS_REVENDA}</td>
                      <td className="p-3.5 text-gray-600">
                        {item.NM_PESQUISA}
                      </td>
                      <td className="p-3.5 text-center font-medium text-gray-700">
                        {item.QTD_CLIENTES_PREVISTOS}
                      </td>
                      <td className="p-3.5 text-center font-medium text-emerald-700 bg-emerald-50/40">
                        <button
                          onClick={() => {
                            setSelectedSellerForItems(item);
                            setIsItemsDialogOpen(true);
                          }}
                          disabled={item.QTD_PESQUISAS_REALIZADAS === 0}
                          className={`px-3 py-1 rounded-md font-bold text-xs transition-all ${
                            item.QTD_PESQUISAS_REALIZADAS > 0
                              ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200 hover:scale-105 shadow-sm cursor-pointer underline underline-offset-2 decoration-emerald-500'
                              : 'text-gray-400 cursor-not-allowed'
                          }`}
                          title={
                            item.QTD_PESQUISAS_REALIZADAS > 0
                              ? 'Clique para visualizar os itens pesquisados'
                              : 'Nenhuma pesquisa realizada'
                          }
                        >
                          {item.QTD_PESQUISAS_REALIZADAS}
                        </button>
                      </td>
                      <td className="p-3.5 text-center font-medium text-amber-700 bg-amber-50/40">
                        {item.QTD_PESQUISAS_PENDENTES}
                      </td>
                      <td className="p-3.5 pr-4 text-right min-w-[140px]">
                        <div className="flex flex-col items-end gap-1">
                          <span className="font-bold text-gray-900">
                            {item.PERC_COBERTURA.toFixed(2)}%
                          </span>
                          <div className="w-24 bg-gray-100 rounded-full h-1.5 overflow-hidden">
                            <div
                              className={`h-full rounded-full ${
                                item.PERC_COBERTURA >= 100
                                  ? 'bg-emerald-500'
                                  : item.PERC_COBERTURA >= 50
                                    ? 'bg-primary'
                                    : 'bg-amber-500'
                              }`}
                              style={{
                                width: `${Math.min(100, item.PERC_COBERTURA)}%`,
                              }}
                            />
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              {/* Footer Totalizers (RF003 / RN002) */}
              {filteredReport.length > 0 && (
                <tfoot>
                  <tr className="font-bold text-gray-900 border-t-2 border-gray-300 bg-gray-100/90">
                    <td colSpan={3} className="p-3.5 pl-4 text-right">
                      TOTAL GERAL ({filteredReport.length} vendedores):
                    </td>
                    <td className="p-3.5 text-center text-gray-900">
                      {totals.previstos}
                    </td>
                    <td className="p-3.5 text-center text-emerald-800">
                      {totals.realizadas}
                    </td>
                    <td className="p-3.5 text-center text-amber-800">
                      {totals.pendentes}
                    </td>
                    <td className="p-3.5 pr-4 text-right text-primary font-extrabold text-base">
                      {totals.cobertura.toFixed(2)}%
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      </div>

      {/* Dialog showing detailed researched items */}
      <SellerCoverageItemsDialog
        isOpen={isItemsDialogOpen}
        onClose={() => {
          setIsItemsDialogOpen(false);
          setSelectedSellerForItems(null);
        }}
        sellerData={selectedSellerForItems}
      />
    </div>
  );
};

export default SellerCoverageReport;
